import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useConnectionDetailApi } from '../../../hooks/useConnectionDetailApi';
import { usePayStatementItemStore } from '../store/pay-statement-store';
import { useParams } from 'react-router-dom';
import { Input, Item } from '../types';
import { baseURL } from '../../../../utils/baseUrls';
import axios from 'axios';
import { analyticsClient } from '../../../../utils/analytics/client';
import { Flex, Text, useToast } from '@chakra-ui/react';

export function useFormControls(initial: Item[]) {
  const { applicationId, connectionId } = useParams<{
    applicationId: string;
    connectionId: string;
  }>();
  const api = useConnectionDetailApi();
  const {
    editing,
    setEditing,
    labels,
    eligible,
    clear,
    setEligible,
    showingSince,
    setShowingSince,
    resetShowingSince,
  } = usePayStatementItemStore();
  const toast = useToast();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: async ({ labels, code }: { labels: Input[]; code?: string }) =>
      api.createPayStatementLabels(applicationId, connectionId, labels, code),
    onSuccess: () => {
      setEditing(false);

      toast({
        variant: 'solid',
        title: 'Payroll mapping has been updated',
        status: 'success',
        duration: 3500,
        render: () => (
          <Flex
            direction={'row'}
            backgroundColor={'#027A48'}
            fontWeight={500}
            textColor={'white'}
            alignItems={'center'}
            gap={'8px'}
            paddingX={'16px'}
            paddingY={'8px'}
            borderRadius={'8px'}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.9375 11L11.8958 6.0625L10.8333 5L6.9375 8.875L5.16667 7.125L4.10417 8.1875L6.9375 11ZM8 16C6.90278 16 5.86806 15.7917 4.89583 15.375C3.92361 14.9583 3.07292 14.3854 2.34375 13.6562C1.61458 12.9271 1.04167 12.0764 0.625 11.1042C0.208333 10.1319 0 9.09722 0 8C0 6.88889 0.208333 5.85069 0.625 4.88542C1.04167 3.92014 1.61458 3.07292 2.34375 2.34375C3.07292 1.61458 3.92361 1.04167 4.89583 0.625C5.86806 0.208333 6.90278 0 8 0C9.11111 0 10.1493 0.208333 11.1146 0.625C12.0799 1.04167 12.9271 1.61458 13.6562 2.34375C14.3854 3.07292 14.9583 3.92014 15.375 4.88542C15.7917 5.85069 16 6.88889 16 8C16 9.09722 15.7917 10.1319 15.375 11.1042C14.9583 12.0764 14.3854 12.9271 13.6562 13.6562C12.9271 14.3854 12.0799 14.9583 11.1146 15.375C10.1493 15.7917 9.11111 16 8 16Z"
                fill="white"
              />
            </svg>
            <Text fontSize={'14px'} fontWeight={'medium'} color={'white'}>
              Payroll mapping has been updated
            </Text>
          </Flex>
        ),
      });

      queryClient.invalidateQueries({
        queryKey: [
          'connection-pay-statement-item',
          applicationId,
          connectionId,
          showingSince,
        ],
      });

      analyticsClient.track('pay_statement_mapping_success');

      clear();
    },
    onError: () => {
      toast({
        variant: 'solid',
        title: 'Failed to update one or more Pay Statement Labels',
        status: 'error',
      });
    },
  });

  const publicSave = useMutation({
    mutationFn: async ({
      labels,
      code,
      pin,
    }: {
      labels: Input[];
      code?: string;
      pin?: string;
    }) => {
      const uri = new URL(
        `/api/v1/employer/${code}/pay-statement-item${
          pin ? `?pin=${pin}` : ''
        }`,
        baseURL,
      );

      try {
        const { data } = await axios.post(uri.toString(), labels);
        return data;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
      return null;
    },
    onSuccess: (data) => {
      setEditing(false);
      toast({
        variant: 'solid',
        title: 'Pay Statement Labels have been successfully updated',
      });
      queryClient.invalidateQueries({
        queryKey: [
          'connection-pay-statement-item',
          applicationId,
          connectionId,
          showingSince,
        ],
      });
      clear();
    },
    onError: (err) => {
      toast({
        variant: 'solid',
        title: 'Failed to update one or more Pay Statement Labels',
        status: 'error',
      });
    },
  });

  return {
    loading: save.isPending,
    editing,
    showingSince,
    setShowingSince: (date: Date) => {
      setShowingSince(date);
      queryClient.invalidateQueries({
        queryKey: [
          'connection-pay-statement-item',
          applicationId,
          connectionId,
          showingSince,
        ],
      });
    },
    resetShowingSince,
    onEdit: () => {
      // set defaults for unmapped items
      for (const item of initial) {
        if (item.category !== 'earnings') {
          continue;
        }

        const mapped =
          item.attributes.metadata !== null &&
          '_RETIREMENT_PLAN_ELIGIBILITY' in item.attributes.metadata &&
          typeof item.attributes.metadata._RETIREMENT_PLAN_ELIGIBILITY ===
            'boolean';

        if (!mapped) {
          setEligible(item.category, item.name, true);
        }
      }

      setEditing(true);
    },
    onCancel: () => {
      setEditing(false);
      clear();
    },
    onSave: async (code?: string, pin?: string) => {
      const payload: Input[] = [];

      for (const item of initial) {
        if (!(item.category in labels || item.category in eligible)) {
          continue;
        }

        const assignedLabel = labels[item.category]?.[item.name];
        const pensionProColumn = item.attributes.metadata
          ? item.attributes.metadata._PENSION_PRO_COLUMN
          : undefined;
        const label =
          assignedLabel === null
            ? undefined
            : assignedLabel ?? pensionProColumn;
        let eligibility = eligible[item.category]?.[item.name];

        if (item.attributes.metadata !== null) {
          eligibility =
            eligibility ??
            item.attributes.metadata._RETIREMENT_PLAN_ELIGIBILITY;

          if (
            label === item.attributes.metadata._PENSION_PRO_COLUMN &&
            eligibility ===
              item.attributes.metadata._RETIREMENT_PLAN_ELIGIBILITY
          ) {
            continue;
          }
        }

        if (eligibility === undefined && !label) {
          continue;
        }

        payload.push({
          category: item.category,
          name: item.name,
          label: assignedLabel ?? undefined,
          eligible: eligibility,
        });
      }

      if (code) {
        await publicSave.mutateAsync({ labels: payload, code, pin });
      } else {
        await save.mutateAsync({ labels: payload });
      }
    },
  };
}
